/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useMemo, useState } from 'react';

import { jsx } from '@compiled/react';

import { fg } from '@atlaskit/platform-feature-flags';
import { componentWithFG } from '@atlaskit/platform-feature-flags-react';

import { useSectionExpansionContext } from '../../../context';
import { useUniversalCreateAnalytics } from '../../../hooks';
import { withIntlProvider } from '../../../services';
import type { RenderableMenuItem, RenderableMenuSectionData } from '../../../types/menuV2';
import MenuItemV2 from '../MenuItemV2';
import MenuSectionPrimitive from '../MenuSectionPrimitive';
import { getFoldableThreshold } from '../MenuSectionPrimitive/utils';

const LEGACY_MAX_ITEMS = 10;
const OVERFLOW_MAX_ITEMS = 50;

export interface RenderableMenuSectionProps {
	section: RenderableMenuSectionData;
	sectionIndex: number;
	sectionStartIndex: number;
	totalMenuItemsCount: number;
}


const RenderableMenuSectionInnerWithFG = componentWithFG('navx-2713-persist-more-state', RenderableMenuSectionInnerV2, RenderableMenuSectionInnerV1)

export const RenderableMenuSection = withIntlProvider(RenderableMenuSectionInnerWithFG);


function RenderableMenuSectionInnerV2({
	section,
	sectionIndex,
	sectionStartIndex,
	totalMenuItemsCount,
}: RenderableMenuSectionProps) {
	const [localShowAll, setLocalShowAll] = useState(false);
	const { fireShowMoreButtonClicked } = useUniversalCreateAnalytics();
	
	// Use persistent state from context, fallback to local state for backwards compatibility
	const { sectionExpansionState, setSectionExpansionState } = useSectionExpansionContext();
	const showAll = sectionExpansionState[sectionIndex] ?? localShowAll;
	const setShowAll = useCallback((value: boolean | ((prev: boolean) => boolean)) => {
		const newValue = typeof value === 'function' ? value(showAll) : value;
		if (fg('navx-2713-persist-more-state')) {
			setSectionExpansionState(sectionIndex, newValue);
		} else {
			setLocalShowAll(newValue);
		}
	}, [setSectionExpansionState, sectionIndex, showAll]);

	const sectionItems = section.items;
	const sectionItemsCount = sectionItems.length;
	const foldableThreshold = getFoldableThreshold(section.foldable);

	const items = useMemo(() => {
		if (!sectionItemsCount) {
			return [];
		}

		if (!section.foldable || sectionItemsCount <= foldableThreshold) {
			return sectionItems;
		}

		const sectionItemsLimit = fg('navx_universal_create_overflow_mode')
			? OVERFLOW_MAX_ITEMS
			: LEGACY_MAX_ITEMS;

		return sectionItems.slice(0, showAll ? sectionItemsLimit : foldableThreshold);
	}, [section.foldable, sectionItems, showAll, sectionItemsCount, foldableThreshold]);

	// Determine if an item is in the "show more" section
	const isItemInMore = (itemIndex: number): boolean => {
		if (!section.foldable || sectionItemsCount <= foldableThreshold) {
			return false;
		}
		return itemIndex >= foldableThreshold;
	};

	const toggleShowMore = useCallback(() => {
		if (typeof section.foldable === 'number' && fg('navx-2610-uc-refactor-show-more')) {
			setShowAll(true); // removing show less functionality under this fg
			fireShowMoreButtonClicked({
				groupHeading: section.heading || 'unknown',
				groupIndex: sectionIndex,
				groupItemCount: sectionItemsCount,
				inMoreItemAtisList: sectionItems
					.slice(foldableThreshold)
					.map((item) => item.ati.toString()),
				inMoreItemCount: sectionItemsCount - foldableThreshold,
			});
		} else {
			setShowAll((prev) => !prev);
		}
	}, [
		setShowAll,
		section,
		sectionIndex,
		sectionItemsCount,
		fireShowMoreButtonClicked,
		foldableThreshold,
		sectionItems,
	]);

	if (!sectionItems?.length) {
		return null;
	}

	return (
		<MenuSectionPrimitive
			sectionIndex={sectionIndex}
			itemLength={sectionItems.length}
			foldable={section.foldable}
			heading={section.heading}
			showAll={showAll}
			toggleShowMore={toggleShowMore}
			isNative
		>
			{items.map((menuItem: RenderableMenuItem, index: number) => (
				<MenuItemV2
					item={menuItem}
					key={`menu-section-${sectionIndex}-item-${index}`}
					itemIndex={index}
					sectionStartIndex={sectionStartIndex}
					sectionItemsCount={sectionItemsCount}
					totalMenuItemsCount={totalMenuItemsCount}
					isInMore={isItemInMore(index)}
					sectionFoldableBehaviour={section.foldable}
				/>
			))}
		</MenuSectionPrimitive>
	);
}

function RenderableMenuSectionInnerV1({
	section,
	sectionIndex,
	sectionStartIndex,
	totalMenuItemsCount,
}: RenderableMenuSectionProps) {
	const [showAll, setShowAll] = useState(false);

	const { fireShowMoreButtonClicked } = useUniversalCreateAnalytics();

	const sectionItems = section.items;
	const sectionItemsCount = sectionItems.length;
	const foldableThreshold = getFoldableThreshold(section.foldable);

	const items = useMemo(() => {
		if (!sectionItemsCount) {
			return [];
		}

		if (!section.foldable || sectionItemsCount <= foldableThreshold) {
			return sectionItems;
		}

		const sectionItemsLimit = fg('navx_universal_create_overflow_mode')
			? OVERFLOW_MAX_ITEMS
			: LEGACY_MAX_ITEMS;

		return sectionItems.slice(0, showAll ? sectionItemsLimit : foldableThreshold);
	}, [section.foldable, sectionItems, showAll, sectionItemsCount, foldableThreshold]);

	// Determine if an item is in the "show more" section
	const isItemInMore = (itemIndex: number): boolean => {
		if (!section.foldable || sectionItemsCount <= foldableThreshold) {
			return false;
		}
		return itemIndex >= foldableThreshold;
	};

	const toggleShowMore = useCallback(() => {
		if (typeof section.foldable === 'number' && fg('navx-2610-uc-refactor-show-more')) {
			setShowAll(true); // removing show less functionality under this fg
			fireShowMoreButtonClicked({
				groupHeading: section.heading || 'unknown',
				groupIndex: sectionIndex,
				groupItemCount: sectionItemsCount,
				inMoreItemAtisList: sectionItems
					.slice(foldableThreshold)
					.map((item) => item.ati.toString()),
				inMoreItemCount: sectionItemsCount - foldableThreshold,
			});
		} else {
			setShowAll((prev) => !prev);
		}
	}, [
		setShowAll,
		section,
		sectionIndex,
		sectionItemsCount,
		fireShowMoreButtonClicked,
		foldableThreshold,
		sectionItems,
	]);

	if (!sectionItems?.length) {
		return null;
	}

	return (
		<MenuSectionPrimitive
			sectionIndex={sectionIndex}
			itemLength={sectionItems.length}
			foldable={section.foldable}
			heading={section.heading}
			showAll={showAll}
			toggleShowMore={toggleShowMore}
			isNative
		>
			{items.map((menuItem: RenderableMenuItem, index: number) => (
				<MenuItemV2
					item={menuItem}
					key={`menu-section-${sectionIndex}-item-${index}`}
					itemIndex={index}
					sectionStartIndex={sectionStartIndex}
					sectionItemsCount={sectionItemsCount}
					totalMenuItemsCount={totalMenuItemsCount}
					isInMore={isItemInMore(index)}
					sectionFoldableBehaviour={section.foldable}
				/>
			))}
		</MenuSectionPrimitive>
	);
}
